import { useCart } from "../context/CartContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import "../styles/components/Checkout.scss";

function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    orderType: "delivery",
    city: "",
    address: "",
  });

  const [customNotes, setCustomNotes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentTransferNumber, setPaymentTransferNumber] = useState("");

  useEffect(() => {
    let active = true;

    api
      .get("/settings")
      .then((res) => {
        if (active) setPaymentTransferNumber(res.data?.payment_transfer_number || "");
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Keyed by "productId-colorId" (not just productId) so the same product
  // added in two different colors gets independent custom-note fields
  // instead of silently sharing/overwriting one.
  const lineKey = (item) => `${item.id}-${item.colorId ?? "none"}`;

  const handleCustomChange = (key, value) => {
    setCustomNotes({
      ...customNotes,
      [key]: value
    });
  };

  const isDelivery = form.orderType === "delivery";
  const paymentLabel = isDelivery ? "Cash on Delivery" : "Cash on Pickup";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!form.name.trim()) return alert("Enter your name");
    if (!form.phone.trim()) return alert("Enter your phone");

    if (isDelivery) {
      if (!form.city.trim()) return alert("Select your city");
      if (!form.address.trim()) return alert("Enter your address");
    }

    const itemsWithNotes = items.map((item) => ({
      id: item.id,
      qty: item.qty,
      note: customNotes[lineKey(item)] || "",
      color_id: item.colorId || undefined,
    }));

    setIsSubmitting(true);
    try {
      const res = await api.post("/orders", {
        name: form.name,
        phone: form.phone,
        order_type: form.orderType,
        city: isDelivery ? form.city : undefined,
        address: isDelivery ? form.address : undefined,
        items: itemsWithNotes
      });

      const confirmation = {
        orderNumber: res.data.order_number,
        totalPrice: res.data.total_price,
        orderType: res.data.order_type,
        items: items.map((item) => ({
          id: item.id,
          name: item.name,
          qty: item.qty,
          price: item.price,
          colorName: item.colorName,
        })),
      };

      clearCart();
      navigate(`/order-success/${res.data.order_id}`, { state: confirmation });
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!items.length) {
    return <div className="checkout-page">Cart is empty</div>;
  }

  return (
    <div className="checkout-page">
      {/* FORM */}
      <form className="checkout-form" onSubmit={handleSubmit}>
        <h2>Checkout</h2>

        <label htmlFor="checkout-name">Full Name</label>
        <input
          id="checkout-name"
          name="name"
          type="text"
          autoComplete="name"
          onChange={handleChange}
        />

        <label htmlFor="checkout-phone">Phone</label>
        <input
          id="checkout-phone"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          onChange={handleChange}
        />

        {/* ORDER TYPE */}
        <h3>Order Type</h3>

        <div className="payment-options">
          <label className="payment-option">
            <input
              type="radio"
              name="orderType"
              value="delivery"
              checked={form.orderType === "delivery"}
              onChange={handleChange}
            />
            <span>Delivery</span>
          </label>

          <label className="payment-option">
            <input
              type="radio"
              name="orderType"
              value="pickup"
              checked={form.orderType === "pickup"}
              onChange={handleChange}
            />
            <span>Pickup</span>
          </label>
        </div>

        {isDelivery ? (
          <>
            <label htmlFor="checkout-city">City</label>
            <select id="checkout-city" name="city" onChange={handleChange}>
              <option value="">Select City</option>
              <option value="Beirut">Beirut</option>
              <option value="South">South</option>
              <option value="Bekaa">Bekaa</option>
              <option value="Jabal">Jabal</option>
              <option value="Shmel">Shmel</option>
            </select>

            <label htmlFor="checkout-address">Address</label>
            <textarea
              id="checkout-address"
              name="address"
              autoComplete="street-address"
              onChange={handleChange}
            />
          </>
        ) : (
          <p className="pickup-note">
            Pickup location: <strong>Beirut, Haret Hreik</strong>.
            You'll collect this order in person. We'll contact you at the
            phone number above to arrange a pickup time.
          </p>
        )}

        {/* PAYMENT INFO */}
        <div className="payment-info">
          <p className="payment-info-label">Payment: {paymentLabel}</p>
          {paymentTransferNumber && (
            <p className="payment-info-note">
              You can also transfer the amount to: <strong>{paymentTransferNumber}</strong>
            </p>
          )}
        </div>

        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? "Placing Order..." : "Place Order"}
        </button>
      </form>

      {/* SUMMARY */}
      <div className="checkout-summary">
        <h3>Summary</h3>

        {items.map((item) => (
          <div key={lineKey(item)} className="row">
            <span>
              {item.name}
              {item.colorName && ` (${item.colorName})`} x {item.qty}
            </span>
            <span>${(item.price * item.qty).toFixed(2)}</span>

            {Boolean(item.customized) && (
              <textarea
                className="custom-note"
                placeholder="Customization (optional)"
                onChange={(e) =>
                handleCustomChange(lineKey(item), e.target.value)
                  }
              />
            )}
          </div>
        ))}

        <div className="total">
          Total: ${subtotal.toFixed(2)}
        </div>
      </div>
    </div>
  );
}

export default Checkout;
